import { useRef, useEffect, useState, useCallback } from 'react';

// Copied from the parent theme for functional parity with the local
// PropertyOverview override. Keep in sync if the base component changes.

interface GlowTraceProps {
  delay?: number;
}

export function GlowTrace({ delay = 0.3 }: GlowTraceProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<SVGRectElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0, perim: 0 });
  const hasAnimated = useRef(false);

  const measure = useCallback(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    if (width > 0 && height > 0) {
      setDims({ w: width, h: height, perim: 2 * (width + height) });
    }
  }, []);

  useEffect(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    if (dims.perim === 0 || hasAnimated.current) return;
    const rect = rectRef.current;
    const wrap = wrapRef.current;
    if (!rect || !wrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const target = wrap.parentElement || wrap;

    const runAnimation = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;

      rect.style.strokeDasharray = `${dims.perim}`;
      rect.style.strokeDashoffset = `${dims.perim}`;
      rect.style.opacity = '0';

      rect.animate(
        [
          { strokeDashoffset: `${dims.perim}`, opacity: 0 },
          { strokeDashoffset: `${dims.perim}`, opacity: 0.85, offset: 0.04 },
          { strokeDashoffset: `${dims.perim * 0.08}`, opacity: 0.85, offset: 0.72 },
          { strokeDashoffset: '0', opacity: 0 },
        ],
        {
          duration: 2200,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          delay: delay * 1000,
          fill: 'forwards',
        }
      );
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      },
      { threshold: 0.3, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [dims.perim, delay]);

  const inset = 1;
  const ready = dims.perim > 0;

  return (
    <div
      ref={wrapRef}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20, overflow: 'visible' }}
    >
      {ready && (
        <svg
          className="glow-trace-svg"
          viewBox={`0 0 ${dims.w} ${dims.h}`}
          preserveAspectRatio="none"
        >
          <rect
            ref={rectRef}
            x={inset}
            y={inset}
            width={dims.w - inset * 2}
            height={dims.h - inset * 2}
            style={{ opacity: 0 }}
          />
        </svg>
      )}
    </div>
  );
}

export default GlowTrace;
